import { array, boolean, number, object, string } from 'yup';

const walletSchema = object({
  id: number().required('Wallet ID is required'),
  is_used: boolean().required('Usage status is required'),
});

const delayRangeSchema = object({
  min: number().required('Minimum delay is required').min(0, 'Minimum delay must be >= 0'),
  max: number()
    .required('Maximum delay is required')
    .min(0, 'Maximum delay must be >= 0')
    .test(
      'min-max',
      'Maximum delay must be greater than or equal to minimum delay',
      function (value) {
        const { min } = this.parent;
        if (value === undefined || min === undefined) return true;
        return value >= min;
      },
    ),
});

// Create conditional wallet schema that validates based on active status
const createConditionalDistributorHolderSchema = (isActive: boolean) =>
  object({
    spending_percent: string()
      .required('Spending percent is required')
      .test('is-valid-percentage', 'Must be a valid percentage (0.00 - 100.00)', value => {
        if (!value) return false;
        const num = parseFloat(value);
        return !isNaN(num) && num >= 0 && num <= 100;
      }),
    wallets: array()
      .of(walletSchema)
      .required('At least one wallet must be selected')
      .test(
        'conditional-minimum',
        'At least one wallet must be selected when task is active',
        function (value) {
          // Only require minimum 1 wallet when active is true
          if (isActive) {
            return value && value.length >= 1;
          }
          return true; // No minimum required when inactive
        },
      ),
  });

const baseSchemaFactory = (values?: any) => {
  const isActive = values?.active ?? true;

  return {
    active: boolean().required('Active status is required'),

    slippage: string()
      .required('Slippage is required')
      .test('is-valid-percentage', 'Must be a valid percentage (0.00 - 100.00)', value => {
        if (!value) return false;
        const num = parseFloat(value);
        return !isNaN(num) && num >= 0 && num <= 100;
      }),

    delay_range: delayRangeSchema.required('Delay range is required'),

    distributors: createConditionalDistributorHolderSchema(isActive).required(
      'Distributors configuration is required',
    ),

    holders: createConditionalDistributorHolderSchema(isActive).required(
      'Holders configuration is required',
    ),
  };
};

// Schema for create mode with dynamic validation based on form values
export const createModeSchema = object()
  .shape(baseSchemaFactory())
  .test(
    'no-duplicate-wallets',
    'The same wallet cannot be in both distributors and holders',
    function (values: any) {
      if (!values) return true;

      const distributorWalletIds = values.distributors?.wallets?.map((w: any) => w.id) || [];
      const holderWalletIds = values.holders?.wallets?.map((w: any) => w.id) || [];

      // Check for duplicates
      const duplicates = distributorWalletIds.filter((id: number) => holderWalletIds.includes(id));

      if (duplicates.length > 0) {
        return this.createError({
          path: 'holders.wallets',
          message: `Wallet${
            duplicates.length > 1 ? 's' : ''
          } cannot be in both distributors and holders: ${duplicates.join(', ')}`,
        });
      }

      return true;
    },
  );

// Schema for edit mode with dynamic validation based on form values
export const editModeSchema = object()
  .shape(baseSchemaFactory())
  .test(
    'no-duplicate-wallets',
    'The same wallet cannot be in both distributors and holders',
    function (values: any) {
      if (!values) return true;

      const distributorWalletIds = values.distributors?.wallets?.map((w: any) => w.id) || [];
      const holderWalletIds = values.holders?.wallets?.map((w: any) => w.id) || [];

      // Check for duplicates
      const duplicates = distributorWalletIds.filter((id: number) => holderWalletIds.includes(id));

      if (duplicates.length > 0) {
        return this.createError({
          path: 'holders.wallets',
          message: `Wallet${
            duplicates.length > 1 ? 's' : ''
          } cannot be in both distributors and holders: ${duplicates.join(', ')}`,
        });
      }

      return true;
    },
  );

// Create dynamic validation schema that depends on current form values
export const createDynamicValidationSchema = (values: any) => {
  return object()
    .shape(baseSchemaFactory(values))
    .test(
      'no-duplicate-wallets',
      'The same wallet cannot be in both distributors and holders',
      function (formValues: any) {
        if (!formValues) return true;

        const distributorWalletIds = formValues.distributors?.wallets?.map((w: any) => w.id) || [];
        const holderWalletIds = formValues.holders?.wallets?.map((w: any) => w.id) || [];

        // Check for duplicates
        const duplicates = distributorWalletIds.filter((id: number) =>
          holderWalletIds.includes(id),
        );

        if (duplicates.length > 0) {
          return this.createError({
            path: 'holders.wallets',
            message: `Wallet${
              duplicates.length > 1 ? 's' : ''
            } cannot be in both distributors and holders: ${duplicates.join(', ')}`,
          });
        }

        return true;
      },
    );
};

// Main validation schema (for backward compatibility)
export const DexNewHoldersBotValidationSchema = createModeSchema;
